import React, { useState, useEffect } from 'react';
import { Button, Input, Spinner, Progress, Form, FormGroup, Label, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import AdaptiveModal from '../../components/Common/AdaptiveModal';
import { workTypesAPI } from '../../helpers/api';

interface WorkType {
  id: string;
  name: string;
  unit: string;
}

interface SiteWork {
  id: string;
  targetQuantity: number | null;
  completedQuantity: number;
  progressPercentage: number | null;
  status: string;
  notes: string | null;
  workType: WorkType;
}

interface Site {
  id: string;
  name: string;
  code: string;
  project: { name: string; city: { name: string } };
}

interface SiteWorksModalProps {
  isOpen: boolean;
  toggle: () => void;
  site: Site | null;
  onUpdate?: () => void;
}

const statusStyles: Record<string, string> = {
  NOT_STARTED: 'bg-secondary bg-opacity-10 text-secondary',
  IN_PROGRESS: 'bg-primary bg-opacity-10 text-primary',
  COMPLETED: 'bg-success bg-opacity-10 text-success',
};

const statusLabels: Record<string, string> = {
  NOT_STARTED: 'Başlamadı',
  IN_PROGRESS: 'Devam Ediyor',
  COMPLETED: 'Tamamlandı',
};

const SiteWorksModal: React.FC<SiteWorksModalProps> = ({
  isOpen,
  toggle,
  site,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [siteWorks, setSiteWorks] = useState<SiteWork[]>([]);
  const [unassignedWorkTypes, setUnassignedWorkTypes] = useState<WorkType[]>([]);
  const [loading, setLoading] = useState(false);

  // Add work modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    workTypeId: '',
    targetQuantity: '',
    notes: '',
  });

  // Cost modal
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [costData, setCostData] = useState<any>(null);
  const [costLoading, setCostLoading] = useState(false);
  const [costSiteWork, setCostSiteWork] = useState<SiteWork | null>(null);

  useEffect(() => {
    if (isOpen && site) {
      fetchData();
    }
  }, [isOpen, site]);

  const fetchData = async () => {
    if (!site) return;

    try {
      setLoading(true);
      const [worksRes, unassignedRes] = await Promise.all([
        workTypesAPI.getSiteWorks(site.id),
        workTypesAPI.getUnassignedForSite(site.id),
      ]);
      setSiteWorks(worksRes.data);
      setUnassignedWorkTypes(unassignedRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!site || !formData.workTypeId) {
      toast.error('İş türü seçin');
      return;
    }

    try {
      setSubmitting(true);
      await workTypesAPI.assignToSite(site.id, {
        workTypeId: formData.workTypeId,
        targetQuantity: formData.targetQuantity ? Number(formData.targetQuantity) : undefined,
        notes: formData.notes || undefined,
      });
      toast.success('İş atandı');
      setAddModalOpen(false);
      setFormData({ workTypeId: '', targetQuantity: '', notes: '' });
      fetchData();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (id: string) => {
    if (!window.confirm('Bu işi şantiyeden kaldırmak istediğinize emin misiniz?')) return;

    try {
      await workTypesAPI.removeSiteWork(id);
      toast.success('İş kaldırıldı');
      fetchData();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Silme başarısız');
    }
  };

  const openCostModal = async (sw: SiteWork) => {
    setCostSiteWork(sw);
    setCostData(null);
    setCostModalOpen(true);
    try {
      setCostLoading(true);
      const response = await workTypesAPI.getSiteWorkCost(sw.id);
      setCostData(response.data);
    } catch (error) {
      toast.error('Maliyet hesaplanamadı');
    } finally {
      setCostLoading(false);
    }
  };

  const getProgressColor = (percentage: number | null) => {
    if (percentage === null) return 'secondary';
    if (percentage >= 100) return 'success';
    if (percentage >= 50) return 'primary';
    if (percentage >= 25) return 'warning';
    return 'danger';
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (!site) return null;

  const headerActions = (
    <>
      <button
        type="button"
        className="side-modal-expand-btn me-1"
        onClick={() => setAddModalOpen(true)}
        title="İş Ekle"
        style={{ color: '#0ab39c' }}
      >
        <i className="ri-add-line"></i>
      </button>
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  return (
    <>
      <SideModal
        isOpen={isOpen}
        toggle={toggle}
        title="Şantiye İşleri"
        size={isExpanded ? 'expanded' : 'lg'}
        headerActions={headerActions}
        hideBackdrop
      >
        <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
          {/* Header */}
          <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }}>
            <h4 className="mb-1" style={{ fontWeight: 600 }}>
              {site.name}
            </h4>
            <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
              {site.project.city.name} • {site.project.name}
            </p>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                  Atanmış İşler
                </span>
                <span className="text-muted small">{siteWorks.length} iş</span>
              </div>
              {siteWorks.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted mb-3">Bu şantiyeye henüz iş atanmamış</p>
                  <Button color="primary" size="sm" onClick={() => setAddModalOpen(true)}>
                    <i className="ri-add-line me-1"></i>
                    İş Ekle
                  </Button>
                </div>
              ) : (
                <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                  {siteWorks.map((sw, idx) => (
                    <div
                      key={sw.id}
                      className="px-3 py-3"
                      style={{ borderBottom: idx < siteWorks.length - 1 ? '1px solid #e9ecef' : 'none' }}
                    >
                      <div className="d-flex align-items-start justify-content-between mb-2">
                        <div>
                          <span style={{ fontWeight: 500 }}>{sw.workType.name}</span>
                          <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
                            ({sw.workType.unit})
                          </span>
                        </div>
                        <div className="d-flex align-items-center">
                          <span
                            className={`px-2 py-1 rounded-pill small me-2 ${statusStyles[sw.status]}`}
                            style={{ fontWeight: 500, fontSize: '0.75rem' }}
                          >
                            {statusLabels[sw.status]}
                          </span>
                          <Button
                            color="warning"
                            size="sm"
                            outline
                            onClick={() => openCostModal(sw)}
                            style={{ padding: '0.15rem 0.4rem' }}
                            className="me-1"
                            title="Maliyet"
                          >
                            <i className="ri-money-dollar-circle-line" style={{ fontSize: '0.8rem' }}></i>
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            onClick={() => handleRemove(sw.id)}
                            style={{ padding: '0.15rem 0.4rem' }}
                          >
                            <i className="ri-delete-bin-line" style={{ fontSize: '0.8rem' }}></i>
                          </Button>
                        </div>
                      </div>
                      {sw.targetQuantity && (
                        <div className="mb-1">
                          <div className="d-flex justify-content-between mb-1">
                            <small className="text-muted">
                              {sw.completedQuantity} / {sw.targetQuantity} {sw.workType.unit}
                            </small>
                            <small className="text-muted">
                              %{sw.progressPercentage?.toFixed(0) || 0}
                            </small>
                          </div>
                          <Progress
                            value={sw.progressPercentage || 0}
                            color={getProgressColor(sw.progressPercentage)}
                            style={{ height: '6px' }}
                          />
                        </div>
                      )}
                      {sw.notes && (
                        <small className="text-muted d-block mt-1">{sw.notes}</small>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </SideModal>

      {/* Add Work Modal */}
      <AdaptiveModal
        isOpen={addModalOpen}
        toggle={() => setAddModalOpen(false)}
        title="İş Ekle"
        size="md"
        footer={
          <>
            <Button color="secondary" onClick={() => setAddModalOpen(false)}>
              İptal
            </Button>
            <Button color="primary" type="submit" form="add-work-form" disabled={submitting}>
              {submitting ? 'Ekleniyor...' : 'Ekle'}
            </Button>
          </>
        }
      >
        <Form id="add-work-form" onSubmit={handleAssign}>
          <FormGroup>
            <Label>İş Türü *</Label>
            <Input
              type="select"
              value={formData.workTypeId}
              onChange={(e) => setFormData({ ...formData, workTypeId: e.target.value })}
              required
            >
              <option value="">İş Türü Seçin</option>
              {unassignedWorkTypes.map((wt) => (
                <option key={wt.id} value={wt.id}>
                  {wt.name} ({wt.unit})
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Hedef Miktar</Label>
            <Input
              type="number"
              value={formData.targetQuantity}
              onChange={(e) => setFormData({ ...formData, targetQuantity: e.target.value })}
              placeholder="Opsiyonel"
            />
          </FormGroup>
          <FormGroup>
            <Label>Not</Label>
            <Input
              type="textarea"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              placeholder="Opsiyonel"
            />
          </FormGroup>
        </Form>
      </AdaptiveModal>

      {/* Cost Detail Modal */}
      <SideModal
        isOpen={costModalOpen}
        toggle={() => setCostModalOpen(false)}
        title={`Maliyet: ${costSiteWork?.workType.name || ''}`}
        size="lg"
        hideBackdrop
      >
        <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {costLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Maliyet hesaplanıyor...</p>
          </div>
        ) : !costData ? (
          <div className="text-center py-5 text-muted">
            <i className="ri-error-warning-line" style={{ fontSize: '2rem' }}></i>
            <p className="mt-2">Maliyet bilgisi yüklenemedi</p>
          </div>
        ) : costData.materials.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="ri-box-3-line" style={{ fontSize: '2rem' }}></i>
            <p className="mt-2">Malzeme eşleştirmesi yok veya eşleşen irsaliye bulunamadı</p>
            <small>İş Türleri sayfasından malzeme eşleştirmesi yapabilirsiniz</small>
          </div>
        ) : (
          <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #0ab39c' }}>
            {/* Özet Kartları */}
            <div className="row g-2 mb-4">
              <div className="col-6">
                <div className="bg-light rounded-3 p-2 text-center">
                  <div className="text-muted small">Malzeme (KDV Hariç)</div>
                  <div style={{ fontWeight: 600, color: '#405189' }}>
                    {costData.totalCost.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                  </div>
                </div>
              </div>
              <div className="col-6">
                <div className="bg-light rounded-3 p-2 text-center">
                  <div className="text-muted small">Birim Maliyet</div>
                  <div style={{ fontWeight: 600, color: '#0ab39c' }}>
                    {costData.unitCost.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺/{costData.unit}
                  </div>
                </div>
              </div>
            </div>

            {/* Detay */}
            <div className="text-muted small mb-2" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              Malzeme Detayları
            </div>
            <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              {costData.materials.map((m: any, idx: number) => (
                <div
                  key={idx}
                  className="px-3 py-2"
                  style={{ borderBottom: idx < costData.materials.length - 1 ? '1px solid #e9ecef' : 'none' }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <span style={{ fontWeight: 500 }}>{m.itemName}</span>
                      <div className="text-muted small">{m.waybillNumber}</div>
                    </div>
                    <div className="text-end">
                      <span style={{ fontWeight: 500 }}>
                        {m.amount.toLocaleString('tr-TR')} {m.unitName || ''}
                      </span>
                      {m.total > 0 && (
                        <div className="text-muted small">
                          {m.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {/* Toplam */}
              <div className="px-3 py-2 d-flex justify-content-between" style={{ borderTop: '2px solid #dee2e6', background: '#e9ecef' }}>
                <span style={{ fontWeight: 600 }}>Toplam (KDV Hariç)</span>
                <span style={{ fontWeight: 600 }}>
                  {costData.totalCost.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                </span>
              </div>
              <div className="px-3 py-2 d-flex justify-content-between">
                <span className="text-muted">KDV</span>
                <span className="text-muted">
                  {costData.totalVat.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                </span>
              </div>
              <div className="px-3 py-2 d-flex justify-content-between" style={{ background: '#d1e7dd' }}>
                <span style={{ fontWeight: 600 }}>Genel Toplam</span>
                <span style={{ fontWeight: 600 }}>
                  {costData.grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                </span>
              </div>
            </div>
          </div>
        )}
        </div>
      </SideModal>
    </>
  );
};

export default SiteWorksModal;
